import { FieldSwitch, FieldType } from './FieldSwitch';
import { Stepper } from './Stepper';
import React, { createRef, useEffect, useRef, useState } from 'react';
import { ZdsAlert, ZdsButton, ZdsForm, ZdsIcon, ZdsTooltip } from '@zig-design-system/ui-components-react';
import { usePowermailSettings } from '../context/PowermailContext';
import { meetsCondition } from '../utilitity/condition';
import Translation from '../../Shared/components/Translation';
import { useForm } from '../context/FormContext';
import { formatValueForValidation, validate } from '../utilitity/validation';
const initFormData = (pages, values) => {
    const formData = {};
    for (const page of pages) {
        for (const field of page.fields) {
            if (values[field.uid]) {
                const value = validate(field.validation)
                    ? formatValueForValidation(field.validation, values[field.uid])
                    : values[field.uid];
                formData[field.name] = { field: field, value: value };
            }
            else if (field.type === FieldType.TEXT) {
                formData[field.name] = { field: field, value: field.text };
            }
            else if (field.type === FieldType.SUBHEADING) {
                formData[field.name] = { field: field, value: field.title };
            }
        }
    }
    return formData;
};
export const Form = ({ pages, afterSteps, activeStep, submitField, action, values, errors }) => {
    const powermailSettings = usePowermailSettings();
    const { uid } = useForm();
    const [step, setStep] = useState(Number(activeStep));
    const [formData, setFormData] = useState(initFormData(pages, values));
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const formRef = useRef(null);
    const containerRef = useRef(null);
    const refs = pages.map(() => ({ desktop: createRef(), mobile: createRef() }));
    useEffect(() => {
        refs.forEach((ref, index) => {
            Object.entries(ref).map(([key, ref]) => {
                if (!ref.current || ref.current.children.length > 0) {
                    return;
                }
                const element = document.getElementById(`page_content_${pages[index].uid}_${key}`);
                if (element === null || element === void 0 ? void 0 : element.firstChild) {
                    ref.current.replaceChildren(element.firstChild.cloneNode(true));
                }
            });
        });
    }, [refs]);
    const totalFileSize = Object.values(uploadedFiles)
        .flat()
        .reduce((sum, item) => sum + item, 0);
    const isExceedingFileSizeLimit = totalFileSize > powermailSettings.maxTotalFileSize * 1024 * 1024;
    const previous = () => {
        setStep(step - 1);
    };
    const cancel = () => {
        if (document.referrer) {
            history.back();
        }
        else {
            window.location.href = '/';
        }
    };
    const submit = () => {
        if (isExceedingFileSizeLimit)
            return;
        // Timeout to wait for all elements to be rendered inside the hidden form
        setTimeout(() => {
            formRef.current.submit();
        }, 100);
    };
    const next = () => {
        if (step < pages.length - 1) {
            setStep(step + 1);
            // Make sure next step has been rendered
            setTimeout(() => {
                var _a;
                (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }, 200);
        }
        else {
            setSubmitting(true);
            // If a confirmation page is active don't execute invisible recaptcha, this will be handled on the confirmation page
            if ((submitField === null || submitField === void 0 ? void 0 : submitField.type) === FieldType.INVISIBLERECAPTCHA && powermailSettings.confirmation !== '1') {
                // The rest of invisible recaptcha handling happens in Resources/Private/Extensions/Powermail/Templates/Form/Form.html
                //@ts-ignore
                window.grecaptcha.execute();
            }
            else {
                submit();
            }
        }
    };
    const onChange = (field, value, fieldValue) => {
        setFormData((formData) => (Object.assign(Object.assign({}, formData), { [field.name]: { field: field, value } })));
        if (field.type === FieldType.FILE) {
            setUploadedFiles((uploadedFiles) => (Object.assign(Object.assign({}, uploadedFiles), { [field.uid]: fieldValue.map((value) => value.size) })));
        }
    };
    const textFieldWithEndform = pages.some((page) => page.fields.some((field) => {
        var _a, _b;
        return field.type === 'text' &&
            field.endForm &&
            (!field.dependency ||
                meetsCondition((_b = (_a = formData[field.dependency.fieldName]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '', field.dependency.operator, field.dependency.value));
    }));
    return (React.createElement("div", { ref: containerRef },
        React.createElement("form", { action: action, method: "POST", id: `powermail_form_${uid}`, ref: formRef, className: "zds-hidden" },
            Object.entries(formData)
                .sort(([, formFieldA], [, formFieldB]) => formFieldA.field.formOrder - formFieldB.field.formOrder)
                .map(([name, formField]) => {
                var _a, _b;
                if (!formField.value ||
                    (formField.field.dependency &&
                        !meetsCondition((_b = (_a = formData[formField.field.dependency.fieldName]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '', formField.field.dependency.operator, formField.field.dependency.value))) {
                    return null;
                }
                if (Array.isArray(formField.value)) {
                    return formField.value.map((value, index) => (React.createElement("input", { key: `${name}_${index}`, name: `${name}[]`, type: "hidden", value: value, readOnly: true })));
                }
                return React.createElement("input", { key: name, name: name, type: "hidden", value: formField.value, readOnly: true });
            }),
            (submitField === null || submitField === void 0 ? void 0 : submitField.type) === FieldType.INVISIBLERECAPTCHA && (React.createElement("input", { id: "response-recaptcha-token", name: "g-recaptcha-response", type: "hidden" })),
            React.createElement("input", { name: 'tx_powermail_pi1[mail][form]', type: "hidden", className: 'powermail_form_uid', value: uid, readOnly: true })),
        afterSteps && (React.createElement(Stepper, { steps: pages, afterSteps: afterSteps, activeStep: step, onNavigate: setStep, erroredSteps: Object.keys(errors) })),
        errors[pages[step].uid] && (React.createElement("div", { className: "zds-row zds-margin-bottom-8" },
            React.createElement("div", { className: "sm:zds-col-10 sm:zds-offset-1 lg:zds-col-6 lg:zds-offset-0" },
                React.createElement(ZdsAlert, { type: "error" },
                    React.createElement(Translation, { item: "alert.warning.title" }),
                    React.createElement("ul", { slot: "body" }, errors[pages[step].uid].map((error) => {
                        const [field, message] = error.split(':', 2);
                        return (React.createElement("li", { key: `${pages[step].uid}-error-${field}` },
                            React.createElement("span", { className: "zds-bold" }, field),
                            ": ",
                            message));
                    })))))),
        pages.map((page, index) => {
            var _a, _b, _c, _d;
            return (React.createElement("div", { className: `zds-row${index !== step ? ' hidden-step' : ''}${powermailSettings.position === 'right' ? ' zds-flex-row-reverse' : ''}${powermailSettings.position === 'center' ? ' zds-justify-content-center' : ''}`, key: `powermail_page_${page.uid}` },
                React.createElement(ZdsForm, { onZdsSubmit: next, className: "sm:zds-col-10 sm:zds-offset-1 lg:zds-col-6 lg:zds-offset-0" },
                    page.css !== 'nolabel' && (React.createElement("h3", { className: "zds-headline-lg zds-margin-top-0 zds-margin-bottom-6" }, page.title)),
                    powermailSettings.position === 'center' && (React.createElement("div", { className: "zds-hidden lg:zds-block zds-margin-y-6", ref: refs[index].desktop })),
                    React.createElement("div", { className: "lg:zds-hidden zds-margin-y-6 lg:zds-margin-0", ref: refs[index].mobile }),
                    React.createElement("div", { className: "zds-flex-col zds-gap-y-4" }, page.fields.map((field) => {
                        var _a, _b, _c;
                        if (field.type === FieldType.SUBMIT ||
                            field.type === FieldType.INVISIBLERECAPTCHA ||
                            (field.dependency &&
                                !meetsCondition((_b = (_a = formData[field.dependency.fieldName]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '', field.dependency.operator, field.dependency.value))) {
                            return null;
                        }
                        return (React.createElement(React.Fragment, { key: `powermail_field_${field.uid}` },
                            React.createElement(FieldSwitch, { field: field, onChange: onChange, value: (_c = formData[field.name]) === null || _c === void 0 ? void 0 : _c.value, submitting: submitting }),
                            field.description && (React.createElement(ZdsTooltip, { target: `#powermail_field_${field.marker}_info`, arrow: true, placement: "right" }, field.description))));
                    })),
                    isExceedingFileSizeLimit && (React.createElement("div", { className: "zds-margin-top-4" },
                        React.createElement(ZdsAlert, { type: "error" },
                            React.createElement(Translation, { item: "powermail.upload.totalSizeLimitExceeded" }),
                            ': ' + powermailSettings.maxTotalFileSize,
                            " MB"))),
                    React.createElement("div", { className: "zds-margin-top-4" }, afterSteps ? (React.createElement("div", { className: `zds-row ${index === 0 && powermailSettings.hideCancelButton ? 'md:zds-flex-row-reverse' : 'md:zds-flex-row'} zds-flex-col-reverse` },
                        index === 0 && !powermailSettings.hideCancelButton && (React.createElement(ZdsButton, { onZdsClick: cancel, variant: "outlined", className: "md:zds-col-6 zds-padding-y-2", id: `powermail_form_${uid}_cancel` },
                            React.createElement(ZdsIcon, { name: index === 0 ? 'xmark' : 'arrow-left', library: "fa-thin", className: "zds-margin-right-2" }),
                            React.createElement(Translation, { item: 'cancelForm' }))),
                        index !== 0 && (React.createElement(ZdsButton, { onZdsClick: previous, variant: "outlined", className: "md:zds-col-6 zds-padding-y-2", id: `powermail_form_${uid}_previous` },
                            React.createElement(ZdsIcon, { name: "arrow-left", library: "fa-thin", className: "zds-margin-right-2" }),
                            React.createElement(Translation, { item: 'previousStep' }))),
                        index === pages.length - 1 && powermailSettings.confirmation !== '1'
                            ? submitField &&
                                (!submitField.dependency ||
                                    meetsCondition((_b = (_a = formData[submitField.dependency.fieldName]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '', submitField.dependency.operator, submitField.dependency.value)) && (React.createElement("div", { className: "md:zds-col-6 zds-padding-y-2" },
                                React.createElement(FieldSwitch, { field: submitField, submitting: submitting })))
                            : !textFieldWithEndform && (React.createElement(ZdsButton, { className: "md:zds-col-6 zds-padding-y-2", type: "submit", id: `powermail_form_${uid}_next` },
                                React.createElement(Translation, { item: 'nextStep' }),
                                React.createElement(ZdsIcon, { name: "arrow-right", library: "fa-thin", className: "zds-margin-left-2" }))))) : (submitField &&
                        (!submitField.dependency ||
                            meetsCondition((_d = (_c = formData[submitField.dependency.fieldName]) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : '', submitField.dependency.operator, submitField.dependency.value)) && (React.createElement("div", { className: "zds-row zds-flex-row-reverse" },
                        React.createElement("div", { className: "md:zds-col-6" },
                            React.createElement(FieldSwitch, { field: submitField, submitting: submitting }))))))),
                powermailSettings.position !== 'center' && (React.createElement("div", { className: "zds-hidden lg:zds-block lg:zds-offset-1 lg:zds-col-5", ref: refs[index].desktop }))));
        })));
};
